import { CHART_COLORS } from "@/constants/colors";

/**
 * Generates a palette of visually distinct colors in hex format.
 * This function uses HSL color space to generate colors evenly distributed across the hue wheel.
 * @param count - Number of colors to generate.
 * @returns string[] Array of hex color codes.
 */
export function generateColors(count: number): string[] {
  const colors: string[] = [];
  for (let i = 0; i < count; i++) {
    // Rotate hue, full saturation, medium lightness
    const hue = Math.round((360 / count) * i);
    colors.push(`hsl(${hue}, 70%, 55%)`);
  }
  return colors;
}

/**
 * Returns a color palette of at least `n` distinct colors.
 * Uses base palette and extends with generated HSL colors if needed.
 */
export function getColorPalette(n: number): string[] {
  if (n <= CHART_COLORS.length) return CHART_COLORS.slice(0, n);
  // Start with base, then add generated (but skip similar hues)
  const extraColors = generateColors(n - CHART_COLORS.length);
  return [...CHART_COLORS, ...extraColors];
}

// Font matching the rendered 12px Plus Jakarta Sans tick text; a complete
// shorthand (size + family) is required or canvas falls back to 10px sans-serif.
export const CHART_AXIS_FONT = '12px "Plus Jakarta Sans", sans-serif';

// Font matching the bold 12px value labels drawn on top of bars.
export const CHART_VALUE_LABEL_FONT =
  '700 12px "Plus Jakarta Sans", sans-serif';

/**
 * Measures the pixel width of `text` at `font`, falling back to a per-character
 * estimate when the canvas 2D context is unavailable (e.g. jsdom).
 *
 * @param text - The text string to be measured.
 * @param font - CSS font descriptor. Defaults to CHART_AXIS_FONT.
 * @returns The width of the text in pixels.
 */
export const measureCanvasTextWidth = (
  text: string,
  font = CHART_AXIS_FONT
): number => {
  const canvas = document.createElement("canvas");
  const context = canvas.getContext("2d");
  if (!context) return 10 * text.length; // Fallback if context is not available
  context.font = font;
  return context.measureText(text).width;
};

// Minimum horizontal breathing room between two neighbouring value labels.
const VALUE_LABEL_GAP = 8;

/**
 * Indexes of the value labels that can be drawn without overlapping. Walks the
 * bars left to right and keeps a label only when it clears the last label still
 * visible — so a hidden label frees the space for the one after it. Values of
 * skipped labels stay reachable through the bar tooltip.
 *
 * @param labels - Formatted label text per bar, in render order.
 * @param slotWidth - Horizontal space one bar owns, in pixels.
 * @returns The set of indexes safe to render.
 */
export const resolveVisibleValueLabels = (
  labels: string[],
  slotWidth: number
): Set<number> => {
  const visible = new Set<number>();
  let lastVisibleRight = -Infinity;

  labels.forEach((label, index) => {
    // Labels are centred on their bar, so they spread half their width either side.
    const center = slotWidth * (index + 0.5);
    const halfWidth = measureCanvasTextWidth(label, CHART_VALUE_LABEL_FONT) / 2;
    if (center - halfWidth < lastVisibleRight + VALUE_LABEL_GAP) return;
    visible.add(index);
    lastVisibleRight = center + halfWidth;
  });

  return visible;
};

/**
 * Returns the maximum pixel width among the `name` values, used to size the
 * Y-axis label column so every label is fully visible.
 *
 * @param data - Array of objects containing a 'name' property.
 * @param font - CSS font descriptor. Defaults to CHART_AXIS_FONT.
 * @param maxLength - Max characters to measure; labels are truncated first.
 * @returns The maximum label width in pixels.
 */
export const getYAxisWidth = (
  data: { name: string }[],
  font = CHART_AXIS_FONT,
  maxLength?: number
): number => {
  return Math.max(
    ...data.map((item) => {
      const name = item.name;
      if (typeof maxLength === "number") {
        return name.length > maxLength
          ? measureCanvasTextWidth(`${name.slice(0, maxLength)}...`, font)
          : measureCanvasTextWidth(name.slice(0, maxLength), font);
      }
      return measureCanvasTextWidth(name, font);
    })
  );
};

/**
 * Wraps `text` into at most `maxLines` lines fitting `maxWidth` px; the last
 * line gets a trailing ellipsis on overflow, or one line when it all fits.
 *
 * @param text - The label to wrap.
 * @param maxWidth - Maximum line width in pixels.
 * @param font - CSS font descriptor. Defaults to CHART_AXIS_FONT.
 * @param maxLines - Maximum number of lines to produce (default 2).
 * @returns Array of display lines (length between 1 and `maxLines`).
 */
export const wrapLabel = (
  text: string,
  maxWidth: number,
  font = CHART_AXIS_FONT,
  maxLines = 2
): string[] => {
  const fits = (value: string) =>
    measureCanvasTextWidth(value, font) <= maxWidth;
  if (maxWidth <= 0 || fits(text)) return [text];

  const words = text.split(/\s+/).filter(Boolean);
  const lines: string[] = [];
  let current = "";
  let overflow = false;

  for (let i = 0; i < words.length; i++) {
    const candidate = current ? `${current} ${words[i]}` : words[i];
    if (fits(candidate)) {
      current = candidate;
      continue;
    }
    // Candidate no longer fits: commit the current line (or the oversized lone
    // word) and continue the next line with the current word.
    lines.push(current || words[i]);
    current = current ? words[i] : "";
    if (lines.length === maxLines) {
      overflow = current !== "" || i < words.length - 1;
      current = "";
      break;
    }
  }
  if (current) lines.push(current);

  const lastIndex = lines.length - 1;
  if (overflow || !fits(lines[lastIndex])) {
    let truncated = lines[lastIndex];
    while (truncated.length > 1 && !fits(`${truncated}...`)) {
      truncated = truncated.slice(0, -1);
    }
    lines[lastIndex] = `${truncated.trimEnd()}...`;
  }

  return lines;
};
